import {Component, Input, OnInit} from '@angular/core';
import {
    IncidentResponse,
    IncidentTransactionsResponse,
    IncidentType,
    Participant,
    ReferenceBookResponse,
    ReferenceBookType,
    RefundsResponse,
    RequisiteType,
    TransactionApiService,
} from '@npck/af-frontend-api';
import {DATE_TIME_SERVER_FORMAT, NESTED_TAB, RouterService, rxSpinner} from '@npck/libs/commons';
import {combineLatest} from 'rxjs';
import {
    fieldExtractor,
    FilterDictionaryField,
    FilterField,
    getFormattedDateOrNull,
} from '../../../../components/full-table/filter/filter.component';
import {initialTableEvent, mergeTableEvent, TableEvent} from '../../../../components/full-table/full-table.component';
import {Column, ColumnType, CustomLayoutColumn, Row} from '../../../../components/full-table/table/table.component';
import {mapTransactionCompletionStatusToStyle, mapTransactionStatusToStyle} from '../../../../components/status/status.component';
import {AuthHolderService} from '../../../../domain/auth-holder.service';
import {DictionaryHolderService, findValueInDictionary} from '../../../../domain/dictionary-holder.service';
import {IncidentDetailsFilterHolderService} from '../../../../domain/incident-details-filter-holder.service';
import {INCIDENT_ID, INCIDENT_TYPE_CODE, TRANSACTION_ID, TRANSACTION_INDEX} from '../../../../domain/query-params.keys';
import {Path} from '../../../../path';
import {IncidentDetailsTab} from '../incident-details.component';
import {MAX_LENGTH_TO_CROP} from '../info/info.component';

@Component({
    selector: 'af-transactions-tab',
    standalone: false,
    templateUrl: './transactions-tab.component.html',
})
export class TransactionsTabComponent implements OnInit {
    @Input() incident: IncidentResponse;
    hasCreateButton: boolean = false;
    filterFields: FilterField[] = [
        {
            type: 'text',
            name: 'query',
        },
        <FilterField>{
            type: 'text',
            name: 'rrn',
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'transactionTypes',
            dictionaryName: ReferenceBookType.TRANSACTION_TYPE,
            values: [],
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'transactionStatuses',
            dictionaryName: ReferenceBookType.TRANSACTION_STATUS,
            values: [],
        },
        <FilterField>{
            type: 'amountRange',
            name: 'amountRange',
            translationPrefix: 'transactions',
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'senderBank',
            dictionaryName: 'bin',
            filterBy: ['BANK', 'PAYM', 'MFO', 'OPERATOR'],
            values: [],
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'receiverBank',
            dictionaryName: 'bin',
            filterBy: ['BANK', 'PAYM', 'MFO', 'OPERATOR'],
            values: [],
        },
        <FilterField>{
            type: 'dateRange',
            name: 'separatedDateRange',
            translationPrefix: 'transactions',
        },
    ];
    dataSource: Row[] = [];
    tableEvent: TableEvent;
    response: RefundsResponse;
    totalElementsForFilter: number;
    totalElements: number;
    isCompromisedPaymentInstrumentType: boolean = false;
    isFraudType: boolean = false;

    constructor(
        private transactionApiService: TransactionApiService,
        private dictionaryHolderService: DictionaryHolderService,
        private routerService: RouterService,
        private authHolderService: AuthHolderService,
        private filterHolderService: IncidentDetailsFilterHolderService,
    ) {
    }

    private get currentNestedTab(): IncidentDetailsTab {
        return this.isCompromisedPaymentInstrumentType ? 'tabCompromisedPaymentInstrument' : 'tabTransactions';
    }

    ngOnInit(): void {
        this.isCompromisedPaymentInstrumentType = this.incident.typeCode === IncidentType.COMPROMISED_PAYMENT_INSTRUMENT;
        this.isFraudType = this.incident.typeCode === IncidentType.FRAUD;
        this.loadTransactions(
            this.filterHolderService.getState(this.incident.id, this.currentNestedTab)?.tableEvent
                ?? this.buildInitialTableEvent(),
        );
    }

    loadTransactions(tableEvent: TableEvent): void {
        const prevEvent = this.filterHolderService.getState(this.incident.id, this.currentNestedTab)?.tableEvent;
        this.tableEvent = mergeTableEvent(prevEvent, tableEvent);
        this.filterHolderService.storeTableState(this.incident.id, this.currentNestedTab, this.tableEvent);
        combineLatest([
            this.dictionaryHolderService.getReference(ReferenceBookType.BIN),
            this.dictionaryHolderService.getReference(ReferenceBookType.CURRENCY),
            this.dictionaryHolderService.getReference(ReferenceBookType.TRANSACTION_COMPLETION_STATUS),
            this.transactionApiService.getIncidentTransactions(this.incident.id, {
                searchQuery: this.tableEvent.filter.query,
                rrn: this.tableEvent.filter.rrn,
                limit: this.tableEvent.page.pageSize,
                offset: this.tableEvent.page.pageOffset,
                sortBy: this.tableEvent.sortBy?.columnName,
                sortOrder: this.tableEvent.sortBy?.direction,
                transactionTypes: fieldExtractor(this.tableEvent.filter?.transactionTypes),
                transactionStatuses: fieldExtractor(this.tableEvent.filter?.transactionStatuses),
                amountFrom: this.tableEvent.filter?.amountRange?.from,
                amountTo: this.tableEvent.filter?.amountRange?.to,
                currency: this.tableEvent.filter?.amountRange?.currency,
                createDateFrom: getFormattedDateOrNull(
                    this.tableEvent.filter?.separatedDateRange?.startDate?.date,
                    DATE_TIME_SERVER_FORMAT,
                ),
                createDateTo: getFormattedDateOrNull(
                    this.tableEvent.filter?.separatedDateRange?.endDate?.date,
                    DATE_TIME_SERVER_FORMAT,
                ),
                senderAccountIssuerBins: fieldExtractor(this.tableEvent.filter?.senderBank),
                receiverAccountIssuerBins: fieldExtractor(this.tableEvent.filter?.receiverBank),
            }),
        ])
            .pipe(rxSpinner(this.tableEvent.spinnerId))
            .subscribe(([organizations, currency, completionStatuses, response]) => {
                this.buildDataSource(response, organizations, currency, completionStatuses);
                const isMyOrganizationRelatedToIncident = [
                    ...this.incident.related,
                    this.incident.governmentBin,
                    this.incident.source_organization
                ]
                    .map(name => findValueInDictionary(organizations, name))
                    .filter(code => this.authHolderService.organizationBin === code)
                    .length > 0;
                this.hasCreateButton = !this.incident.isDeleted && !this.incident.isInnocentStatus
                    && this.incident.typeCode !== IncidentType.MSISDN_FRAUD
                    && (this.authHolderService.hasAccess(['npck-admin']) || isMyOrganizationRelatedToIncident);
            });
    }

    navigateToCreateTransaction(): void {
        this.routerService.navigate(Path.INCIDENTS_TRANSACTIONS_CREATE, {
                pathParams: {
                    [INCIDENT_ID]: this.incident.id,
                },
                queryParams: {
                    [NESTED_TAB]: this.currentNestedTab,
                    [TRANSACTION_INDEX]: this.totalElements,
                    [INCIDENT_TYPE_CODE]: this.incident.typeCode,
                },
                queryParamsHandling: 'merge',
            },
        );
    }

    private buildInitialTableEvent(): TableEvent {
        return {
            ...initialTableEvent,
            spinnerId: 'transactions',
            filter: {
                query: '',
            },
            sortBy: {
                columnName: 'date',
                direction: 'asc',
            },
        };
    }

    private buildDataSource(
        response: IncidentTransactionsResponse,
        organizations: ReferenceBookResponse[],
        currency: ReferenceBookResponse[],
        completionStatuses: ReferenceBookResponse[],
    ): void {
        this.totalElementsForFilter = response.total;
        this.totalElements = response.totalTransactions;
        this.dataSource = response.transactions.map((item, index) => {
            const row: Row = {
                click: () => this.navigateToTransactionDetails(index, item.id),
                button: this.incident.isDeleted || this.incident.isInnocentStatus || item.isEditable === false
                    ? <Column>{type: ColumnType.HIDDEN}
                    : <CustomLayoutColumn>{
                        type: ColumnType.CUSTOM_LAYOUT,
                        cellStyle: {
                            layoutDirection: 'row',
                            layoutAlignFirst: 'start',
                            layoutAlignSecond: 'center',
                            layoutGap: '8px',
                        },
                        layoutContentItems: [
                            {
                                type: 'icon',
                                text: 'ic_edit',
                                click: () => this.editTransaction(index, item.id),
                                tooltip: 'i18n_af_labelEdit',
                                iconSize: 'i-16',
                            },
                        ],
                    },
                date: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.date,
                },
                type: <Column>{
                    type: ColumnType.TEXT,
                    text: item.type ?? '-',
                    cropText: item.type?.length > MAX_LENGTH_TO_CROP,
                },
                ...(this.isFraudType ? {
                    completionStatus: <Column>{
                        type: ColumnType.STATUS,
                        text: findValueInDictionary(completionStatuses, item.completionStatus, 'name_ru') ?? '-',
                        style: mapTransactionCompletionStatusToStyle(item.completionStatus),
                    }
                } : {
                    status: <Column>{
                        type: ColumnType.STATUS,
                        text: item.status ?? '-',
                        style: mapTransactionStatusToStyle(item.status),
                    },
                }),
                amount: item.amount == null
                    ? <Column>{
                        type: ColumnType.TEXT,
                        text: '-',
                    }
                    : <Column>{
                        type: ColumnType.MONEY,
                        text: item.amount.toString(),
                    },
                currency: <Column>{
                    type: ColumnType.TEXT,
                    text: findValueInDictionary(currency, item.currency, 'code1') ?? '-',
                },
                ...(this.isCompromisedPaymentInstrumentType ? {} : {
                    bankSender: {
                        type: ColumnType.TEXT,
                        text: this.getParticipantAccountOrganization(item.sender, organizations),
                    },
                }),
                bankReceiver: <Column>{
                    type: ColumnType.TEXT,
                    text: this.getParticipantAccountOrganization(item.receiver, organizations),
                },
                ...(this.isCompromisedPaymentInstrumentType ? {} : {
                    identificationNumberSender: {
                        type: ColumnType.TEXT,
                        text: item.sender.identification_number,
                    },
                }),
                identificationNumberReceiver: <Column>{
                    type: ColumnType.TEXT,
                    text: item.receiver.identification_number,
                },
                rrn: <Column>{
                    type: ColumnType.TEXT,
                    text: item.transactionRrn,
                },
                id: <Column>{
                    type: ColumnType.TEXT,
                    text: item.id,
                    cellStyle: {
                        ellipsisTextLarge: true,
                    },
                },
            };
            return row;
        });
    }

    private getParticipantAccountOrganization(participant: Participant, organizations: ReferenceBookResponse[]): string {
        let bin: string;
        switch (participant.requisiteType) {
            case RequisiteType.ACCOUNT_FOREIGN_BANK:
                bin = participant.foreignAccount.issuerBin;
                break;
            case RequisiteType.ACCOUNT_KZ:
                bin = participant.account.issuerBin;
                break;
            case RequisiteType.CARD_MASK:
                bin = participant.card_mask.issuer_bin;
                break;
            case RequisiteType.ELECTRONIC_WALLET:
                bin = participant.wallet.issuer_bin;
                break;
            case RequisiteType.PAYMENT_POINT:
                bin = participant.paymentPoint.issuer_bin;
                break;
        }
        return organizations.find(dictionary => bin && (dictionary.code === bin || dictionary.code2 === bin))?.name_ru ?? bin ?? '-';
    }

    private navigateToTransactionDetails(index: number, transactionId: string): void {
        this.routerService.navigate(Path.INCIDENTS_TRANSACTIONS_DETAILS, {
                pathParams: {
                    [INCIDENT_ID]: this.incident.id,
                    [TRANSACTION_ID]: transactionId,
                },
                queryParams: {
                    [NESTED_TAB]: this.currentNestedTab,
                    [TRANSACTION_INDEX]: index,
                },
                queryParamsHandling: 'merge',
            },
        );
    }

    private editTransaction(index: number, transactionId: string): void {
        this.routerService.navigate(Path.INCIDENTS_TRANSACTIONS_EDIT, {
                pathParams: {
                    [INCIDENT_ID]: this.incident.id,
                    [TRANSACTION_ID]: transactionId,
                },
                queryParams: {
                    [NESTED_TAB]: this.currentNestedTab,
                    [TRANSACTION_INDEX]: index,
                    [INCIDENT_TYPE_CODE]: this.incident.typeCode,
                },
                queryParamsHandling: 'merge',
            },
        );
    }
}
